// Desc: Admin Overview page
import Layout from "../../../components/Layout";
import PageTitle from "../../../components/PageTitle";
import { httprequest } from "../../../data/api";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import Table from "../../../components/Table/Table";
import TabContext from "@mui/lab/TabContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { TableSearch } from "../../../components/Table/TableActions";
import PageLoader from "../../../components/PageLoader";
import useWindowSize from "../../../hooks/useWindow";
import Modal from "../../../components/Modal";
import Badge from "../../../components/Badge";

const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const GET_SESSIONS = "/api/v1/school-plan/session";
const STUDENT_RESULT = "/api/v1/ca-test/student/result";

const AdminCA = () => {
  const [activeSession, setActiveSession] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [aselectedTerm, setASelectedTerm] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [itemModal, setItemModal] = useState(false);
  const [allCAResults, setAllTestResults] = useState([]);
  const [testresultDetails, setTestResultsDetails] = useState([]);
  const [searchtestresultDetails, setSearchTestResultsDetails] = useState([]);
  const [selectedSessionTerms, setSelectedSessionTerms] = useState([]);

  const [selectedRow, setSelectedRow] = useState({});
  const [getIsLoading, setGetIsLoading] = useState(false);
  const [isTermLoading, setIsTermLoading] = useState(false);
  const width = useWindowSize();
  const isMobile = width < 600;

  /**
 * Fetches all terms for a given session and filters them by session ID
 * @param {string} Session_ID - The ID of the session to filter terms by
 * @async
 */
  const getAllTerms = (Session_ID) => {
    setIsTermLoading(true);
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        setSelectedSessionTerms(res?.data?.data.filter((selected_term) => selected_term?.session_id === Session_ID));
        setIsTermLoading(false);
      });
    // .catch(() => {});
  };

  /**
 * Handles opening of the item modal and sets the selected row data
 * @param {Object} row - The row data containing test result details
 */
  const handleOpenItemMOdal = (row) => {
    setSelectedRow(row);
    setItemModal(!itemModal);
    setTestResultsDetails(row?.details);
    setSearchTestResultsDetails(row?.details);
  };

  /**
 * Handles closing of the result modal
 */
  const handleCloseResult = () => {
    setItemModal(false);
  };

  /**
 * Fetches school test results for a selected term
 * @param {string} aselectedTerm - The ID of the selected term
 * @async
 */
  const getSchoolTestResult = (aselectedTerm) => {
    setGetIsLoading(true);
    httprequest
      .get(`${STUDENT_RESULT}?term_id=${aselectedTerm}`)
      .then((res) => {
        setAllTestResults(res?.data?.data);
        setSearchResult(res?.data?.data);
        setGetIsLoading(false);
      }).catch((err) => {
        setGetIsLoading(false);
        toast.error(err?.response?.message);
      });
  };

  /**
 * Fetches all available sessions and sets the active session
 * @async
 */
  const getAllSessions = async () => {
    try {
      const res = await httprequest.get(GET_SESSIONS);
      setAllSessions(res?.data?.data);
      setActiveSession(res?.data?.data?.find((active_session) => active_session?.current_session === true));
    } catch (err) {
      toast.error(err?.response?.message);
    }
  };

  /**
 * The currently active term in the current session
 * @type {Object}
 */
  const active_term_session = activeSession?.terms?.find((found_active_term) => found_active_term?.is_current === true);

  /**
 * Handles session change events
 * @param {Event} e - The change event from the session select input
 */
  const handleSessionChange = (e) => {
    const Session_ID = e.target.value;
    getAllTerms(Session_ID);
  };

  /**
 * Handles term change events
 * @param {Event} event - The change event from the term select input
 */
  const handleTermChange = (event) => {
    const selectedTermId = event.target.value;

    setASelectedTerm(selectedTermId);
    getSchoolTestResult(selectedTermId);
  };

  const uniqueCATypes = useMemo(() => {
    return Array.from(
      new Set(
        searchResult?.flatMap(result => Object.keys(result?.ca_totals || {})
        )
      )
    );
  }, [searchResult]);
  const [caValue, setCaValue] = useState(uniqueCATypes?.[0] || "");

  /**
 * Handles CA type change events
 * @param {Event} event - The change event
 * @param {string} newValue - The new CA value selected
 */
  const handleCAChange = (event, newValue) => {
    setCaValue(newValue);
  };

  useEffect(() => {
    if (uniqueCATypes.length && !caValue) {
      setCaValue(uniqueCATypes[0]);
    }
  }, [uniqueCATypes, caValue]);

  /**
 * Columns configuration for the main results table
 * @type {Array<Object>}
 * @property {string} name - Column header name
 * @property {function} selector - Function to render cell content
 * @property {boolean} [center] - Whether to center-align content
 * @property {boolean} [sortable] - Whether column is sortable
 * @property {function} [cell] - Custom cell renderer function
 */
  const columns = [
    {
      name: "Class",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Class</p>}
          {row?.class?.name}
        </>
      )
    },
    {
      name: "Subject",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Subject</p>}
          {row?.subject?.name || "--/--"}
        </>
      )
    },
    ...(
      Array.from(
        new Set(
          searchResult?.flatMap(result => Object.keys(result?.ca_totals || {})
          )
        )
      ).map((caKey) => ({
        name: caKey,
        selector: (row) => (
          <>
            {isMobile && <p className="mobile-styling">{caKey}</p>}
            {row?.ca_totals?.[caKey] ?? "--/--"}
          </>
        )
      }))
    ),
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row, index) => (
        <div key={index} className="table-action-container">
          <button
            onClick={() => handleOpenItemMOdal(row)}
            className={"table-action-icon"}
          >
            <Icon icon="iconamoon:question-mark-circle" width="20" height="20" />
          </button>
        </div>
      )
    }
  ];

  /**
 * Columns configuration for the test result details table
 * @type {Array<Object>}
 */
  const testresultdetailscolumn = [
    {
      name: "Test Title",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Test Title</p>}
          {row?.title}
        </>
      )
    },
    {
      name: "Score",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Score</p>}
          {row?.total_grade || "--/--"}
        </>
      )
    },
    {
      name: "Status",
      center: true,
      cell: (row) => <Badge status={row.status?.toLowerCase()} />
    }
  ];

  /**
 * Filters test result details table using search input
 * @param {Event} e - The input change event
 */
  const handleSearchTestResult = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = testresultDetails?.filter((data) => valArray?.every(
      (word) => data?.title
        ?.toLowerCase()
        .includes(word.toLowerCase()) ||
                                      data?.status
                                        ?.toLowerCase()
                                        .includes(word.toLowerCase()) ||
                                      data?.category
                                        ?.toLowerCase()
                                        .includes(word.toLowerCase()) ||
                                        data?.status
                                          ?.toLowerCase()
                                          .includes(word.toLowerCase())
    )
    );
    setSearchTestResultsDetails(result);
  };

  /**
 * Filters student results table using search input
 * @param {Event} e - The input change event
 */
  const handleSearchStudent = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = allCAResults?.filter((data) => valArray?.every(
      (word) => data?.student?.last_name
        ?.toLowerCase()
        .includes(word.toLowerCase()) ||
                          data?.subject?.name
                            ?.toLowerCase()
                            .includes(word.toLowerCase()) ||
                          data?.ca1
                            ?.toLowerCase()
                            .includes(word.toLowerCase()) ||
                            data?.ca2
                              ?.toLowerCase()
                              .includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  /**
 * Effect hook that runs on component mount to fetch initial data
 */
  useEffect(() => {
    getAllSessions();
    getAllTerms();
  }, []);

  /**
 * Effect hook that sets and loads data for active term when it changes
 */
  useEffect(() => {
    if (active_term_session?.id) {
      setASelectedTerm(active_term_session?.id);
      getSchoolTestResult(active_term_session?.id);
    }
  }, [active_term_session]);

  /**
 * Effect hook that fetches terms when active session changes
 * @effect
 * @listens activeSession
 */
  useEffect(() => {
    if (activeSession?.id) {
      // setSelectedSession(activeSession?.id);
      getAllTerms(activeSession?.id);
    }
  }, [activeSession]);

  return (
    <Layout children={
      <>
        <PageTitle
          title="CA Results"
          subtitle="See all CA Results information"
        />

        <div className="flex gap-10 align-items-end margin-top-5 margin-bottom">
          <div>
            {activeSession?.id && (
              <SelectGroup
                children={
                  <select
                    defaultValue={activeSession?.id}
                    onChange={handleSessionChange}
                    name="session"
                  >
                    <option value="">---Select---</option>
                    {allSessions?.map((sesh) => {
                      return (
                        <option key={sesh?.id} value={sesh?.id}>
                          {sesh?.session}
                        </option>
                      );
                    })}
                  </select>
                }
              />
            )}
          </div>
          <div>
            {active_term_session?.id && (
              <SelectGroup
                children={
                  <select onChange={handleTermChange} name="term" value={aselectedTerm}>
                    <option value="">{isTermLoading ? "Please wait..." : "--Select--"}</option>
                    {selectedSessionTerms?.map((data) => {
                      return (
                        <option key={data?.id} value={data?.id}>
                          {data?.term}
                        </option>
                      );
                    })}
                  </select>
                }
              />
            )}
          </div>
        </div>

        <div className="page-content">
          <>
            {getIsLoading ? (
              <PageLoader />
            ) : (
              <Table
                data={searchResult}
                columns={columns}
                subHeader={true}
                pagination
                subHeaderComponent={
                  <div className="action_div">
                    <TableSearch
                      placeholder="Search here..."
                      searchTableFunc={handleSearchStudent}
                    />
                  </div>}
              />
            )}
          </>
        </div>
        {/* View Log Option */}
        <Modal
          title={`${selectedRow?.subject?.name} CA Details`}
          subtitle={
            <div>
                You can view all the CA Results for this subject.
            </div>
          }
          modalContent={
            <>
              <div className="table-border">
                <TabContext value={caValue}>
                  <div className="padding-left-16">
                    <Tabs
                      className="overide-tablist"
                      onChange={handleCAChange}
                      aria-label="CA Tabs"
                    >
                      {uniqueCATypes.map((ca, i) => (
                        <Tab key={i} value={ca} label={ca} />
                      ))}
                    </Tabs>
                  </div>

                  {uniqueCATypes?.map((ca, i) => {
                    const filteredData = searchtestresultDetails.filter((item) => item?.ca_type === ca);

                    return (
                      <TabPanel className="tabpanel-overide" key={i} value={ca}>
                        <Table
                          data={filteredData}
                          columns={testresultdetailscolumn}
                          subHeader={true}
                          pagination
                          subHeaderComponent={
                            <TableSearch
                              placeholder="Search here..."
                              searchTableFunc={handleSearchTestResult}
                            />
                          }
                        />
                      </TabPanel>
                    );
                  })}
                </TabContext>
              </div>
            </>
          }
          modalFooter={
            <>
            </>
          }
          isopen={itemModal}
          closeModal={handleCloseResult}
        />
        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default AdminCA;
